const DEFAULT_MIN_QUANTITY = 1000;
const DEFAULT_MAX_QUANTITY = 50000;
const STORE_COPY_STEP = 1000;

type RRDStoreCopyConfig = Record<string, any>;

type RRDStoreCopyResolveArgs = {
  location?: RRDStoreCopyConfig;
  campaign?: RRDStoreCopyConfig;
};

type RRDStoreCopyQuantityArgs = {
  storeCopyQnt?: any;
  storeCopy?: any;
  config?: RRDStoreCopyConfig;
};

function toNumber(value: any) {
  if (value === null || value === undefined || value === "") return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function isValidQuantityBoundary(value: any) {
  return Number.isFinite(value) && value > 0 && value % STORE_COPY_STEP === 0;
}

function formatQuantity(value: any) {
  return Number(value || 0).toLocaleString("en-US");
}

function normalizeApiConfig(rawConfig: RRDStoreCopyConfig, source: string) {
  if (!rawConfig) {
    return null;
  }

  const cpm = toNumber(rawConfig.cpm);
  const minQuantity = toNumber(rawConfig.min_quantity);
  const maxQuantity = toNumber(rawConfig.max_quantity);

  const isConfigured =
    cpm !== null &&
    cpm >= 0 &&
    isValidQuantityBoundary(minQuantity) &&
    isValidQuantityBoundary(maxQuantity) &&
    minQuantity <= maxQuantity;

  if (!isConfigured) {
    return null;
  }

  return {
    cpm,
    minQuantity,
    maxQuantity,
    isConfigured,
    source,
  };
}

function normalizeFlatFormConfig(
  rawConfig: RRDStoreCopyConfig,
  source: string
) {
  if (!rawConfig) {
    return null;
  }

  return normalizeApiConfig(
    {
      cpm: rawConfig.store_copy_cpm,
      min_quantity: rawConfig.store_copy_min_quantity,
      max_quantity: rawConfig.store_copy_max_quantity,
    },
    source
  );
}

function hasFlatConfig(source: RRDStoreCopyConfig = {}) {
  return [
    source.store_copy_cpm,
    source.store_copy_min_quantity,
    source.store_copy_max_quantity,
  ].some((value) => value !== null && value !== undefined && value !== "");
}

export function resolveRRDStoreCopyConfig({
  location = {},
  campaign = {},
}: RRDStoreCopyResolveArgs = {}) {
  const locationConfig = normalizeApiConfig(
    location.store_copy_config,
    "location"
  );
  if (locationConfig) return locationConfig;

  const flatLocationConfig = hasFlatConfig(location)
    ? normalizeFlatFormConfig(location, "location")
    : null;
  if (flatLocationConfig) return flatLocationConfig;

  const legacyCpm = toNumber(location.store_copy_cpm ?? campaign.storeCopyCpm);
  if (legacyCpm !== null && legacyCpm >= 0) {
    return {
      cpm: legacyCpm,
      minQuantity: DEFAULT_MIN_QUANTITY,
      maxQuantity: DEFAULT_MAX_QUANTITY,
      isConfigured: true,
      source: "legacy",
    };
  }

  return {
    cpm: 0,
    minQuantity: DEFAULT_MIN_QUANTITY,
    maxQuantity: DEFAULT_MAX_QUANTITY,
    isConfigured: false,
    source: "missing",
  };
}

export function getRRDStoreCopyFormFields(config: RRDStoreCopyConfig = {}) {
  return {
    store_copy_cpm: config.cpm || 0,
    store_copy_min_quantity: config.minQuantity || "",
    store_copy_max_quantity: config.maxQuantity || "",
    store_copy_fixed_quantity: isRRDStoreCopyFixedQuantity(config),
  };
}

export function isRRDStoreCopyFixedQuantity(config: RRDStoreCopyConfig = {}) {
  return (
    config.isConfigured &&
    isValidQuantityBoundary(config.minQuantity) &&
    config.minQuantity === config.maxQuantity
  );
}

export function getRRDStoreCopyDefaultQuantity(
  config: RRDStoreCopyConfig = {}
) {
  return isRRDStoreCopyFixedQuantity(config) ? config.minQuantity : "";
}

export function validateRRDStoreCopyQuantity({
  storeCopyQnt,
  storeCopy,
  config,
}: RRDStoreCopyQuantityArgs = {}) {
  if (!storeCopy) {
    return "";
  }

  const resolvedConfig = config?.isConfigured
    ? config
    : resolveRRDStoreCopyConfig({ location: config || {} });

  if (!resolvedConfig?.isConfigured) {
    return "Store copy configuration is missing";
  }

  const stringValue = String(storeCopyQnt ?? "");
  const numValue = Number(storeCopyQnt);

  if (
    storeCopyQnt === null ||
    storeCopyQnt === undefined ||
    stringValue.trim() === "" ||
    numValue === 0 ||
    Number.isNaN(numValue)
  ) {
    return "Quantity is required";
  }

  if (
    isRRDStoreCopyFixedQuantity(resolvedConfig) &&
    numValue !== resolvedConfig.minQuantity
  ) {
    return `Quantity must be ${formatQuantity(resolvedConfig.minQuantity)}`;
  }

  if (
    numValue < resolvedConfig.minQuantity ||
    numValue > resolvedConfig.maxQuantity
  ) {
    return `Quantity must be between ${formatQuantity(
      resolvedConfig.minQuantity
    )} and ${formatQuantity(resolvedConfig.maxQuantity)}`;
  }

  if (numValue % STORE_COPY_STEP !== 0 || numValue <= 0) {
    return "Quantity must be in increments of 1,000";
  }

  return "";
}

export function calculateRRDStoreCopyCost({
  storeCopy = false,
  storeCopyQnt = 0,
  config,
}: RRDStoreCopyQuantityArgs = {}) {
  const resolvedConfig = config?.isConfigured
    ? config
    : resolveRRDStoreCopyConfig({ location: config || {} });
  const error = validateRRDStoreCopyQuantity({
    storeCopyQnt,
    storeCopy,
    config: resolvedConfig,
  });

  if (error) {
    return 0;
  }

  return (
    (Number(storeCopyQnt) / STORE_COPY_STEP) * Number(resolvedConfig.cpm || 0)
  );
}
